import React, { useEffect, useState, useCallback } from "react";
import * as A from "./lib/api";
import { H1, H2, Sub, Card } from "./Pages";
import { toast } from "sonner";
import { Users2, Plus, Trash2, AlertTriangle, CheckCircle2, ScrollText, FileWarning } from "lucide-react";

const money = (n) => "$" + Number(n || 0).toLocaleString();
const bChip = (s) => {
  const tone = s === "Confirmation Received" ? "green" : s === "Institution Accepted" ? "green" : s === "Superseded" ? "gray" : "amber";
  return <span className={`chip chip-${tone} text-xs`}>{s}</span>;
};

// ==================== BENEFICIARY DESIGNATIONS (§11) ====================
export function BeneficiaryPage() {
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    const [r, s] = await Promise.all([A.reg.list("beneficiary_accounts"), A.beneficiarySummary()]);
    setRows(r.data); setSummary(s.data); setStatuses(s.data.statuses);
  }, []);
  useEffect(() => { load(); }, [load]);

  const blank = { AccountType: "", Institution: "", Owner: "", Insured: "", Status: "Planning Worksheet",
    LastConfirmed: "", Source: "", Notes: "", Beneficiaries: [] };
  const startNew = () => setEditing({ ...blank });
  const addBen = () => setEditing({ ...editing, Beneficiaries: [...(editing.Beneficiaries || []), { name: "", type: "primary", percent: "", relationship: "", is_minor: false, special_needs: false }] });
  const setBen = (i, k, v) => { const b = [...editing.Beneficiaries]; b[i] = { ...b[i], [k]: v }; setEditing({ ...editing, Beneficiaries: b }); };
  const delBen = (i) => setEditing({ ...editing, Beneficiaries: editing.Beneficiaries.filter((_, x) => x !== i) });

  const primaryTotal = (editing?.Beneficiaries || []).filter(b => (b.type || "primary") === "primary").reduce((s, b) => s + Number(b.percent || 0), 0);
  const contingentTotal = (editing?.Beneficiaries || []).filter(b => b.type === "contingent").reduce((s, b) => s + Number(b.percent || 0), 0);

  const save = async () => {
    const payload = { ...editing, Beneficiaries: (editing.Beneficiaries || []).map(b => ({ ...b, percent: Number(b.percent || 0) })) };
    try {
      if (payload.id) { const id = payload.id; delete payload.id; await A.reg.patch("beneficiary_accounts", id, payload); }
      else await A.reg.create("beneficiary_accounts", payload);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const del = async (id) => { if (window.confirm("Delete this designation?")) { await A.reg.del("beneficiary_accounts", id); load(); } };

  const complete = (summary?.accounts || []).filter(a => a.complete).length;
  const withFlags = (summary?.accounts || []).filter(a => a.flags.length).length;

  return (
    <div data-testid="beneficiary-page">
      <H1>Beneficiary Designations</H1>
      <Sub>Track account and policy beneficiaries with primary/contingent percentage validation. A designation is NOT complete until the institution accepts it and confirmation is recorded — the Portal never marks a change complete on your say-so alone.</Sub>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card><div className="label-caps">Accounts tracked</div><div className="font-display text-3xl mt-1" style={{ color: 'var(--primary)' }}>{summary.accounts.length}</div></Card>
          <Card><div className="label-caps">Institution-confirmed</div><div className="font-display text-3xl mt-1" style={{ color: 'var(--gold-hover)' }}>{complete}</div></Card>
          <Card><div className="label-caps">Need attention</div><div className="font-display text-3xl mt-1" style={{ color: withFlags ? 'var(--overdue)' : 'var(--primary)' }}>{withFlags}</div></Card>
          <Card><div className="label-caps flex items-center gap-1"><FileWarning size={12}/> Rule</div><div className="text-xs mt-1" style={{ color: 'var(--muted)' }}>Complete only at "Confirmation Received"</div></Card>
        </div>
      )}

      <div className="flex justify-between items-center mb-3"><H2>Designations</H2><button className="btn-gold flex items-center gap-1" onClick={startNew} data-testid="ben-new"><Plus size={14}/> New designation</button></div>

      {editing && (
        <Card className="mb-4" data-testid="ben-form">
          <div className="grid md:grid-cols-3 gap-3 mb-3">
            <input placeholder="Account/Policy type (IRA, 401k, Life...)" value={editing.AccountType} onChange={e=>setEditing({...editing,AccountType:e.target.value})} data-testid="ben-type"/>
            <input placeholder="Institution" value={editing.Institution} onChange={e=>setEditing({...editing,Institution:e.target.value})} data-testid="ben-institution"/>
            <input placeholder="Owner / Insured" value={editing.Owner} onChange={e=>setEditing({...editing,Owner:e.target.value})}/>
            <select value={editing.Status} onChange={e=>setEditing({...editing,Status:e.target.value})} data-testid="ben-status">{statuses.map(s=><option key={s}>{s}</option>)}</select>
            <input type="date" value={editing.LastConfirmed||""} onChange={e=>setEditing({...editing,LastConfirmed:e.target.value})}/>
            <input placeholder="Source / evidence" value={editing.Source} onChange={e=>setEditing({...editing,Source:e.target.value})}/>
          </div>

          <div className="flex justify-between items-center mb-2">
            <div className="label-caps">Beneficiaries</div>
            <div className="text-xs">
              Primary: <b style={{color: Math.abs(primaryTotal-100)<0.01?'var(--gold-hover)':'var(--overdue)'}}>{primaryTotal}%</b>
              {(editing.Beneficiaries||[]).some(b=>b.type==="contingent") && <> · Contingent: <b style={{color: Math.abs(contingentTotal-100)<0.01?'var(--gold-hover)':'var(--overdue)'}}>{contingentTotal}%</b></>}
            </div>
          </div>
          {(editing.Beneficiaries||[]).map((b,i)=>(
            <div key={i} className="grid grid-cols-12 gap-2 mb-2 items-center" data-testid={`ben-line-${i}`}>
              <input className="col-span-4" placeholder="Name" value={b.name} onChange={e=>setBen(i,"name",e.target.value)} data-testid={`ben-line-name-${i}`}/>
              <select className="col-span-3" value={b.type} onChange={e=>setBen(i,"type",e.target.value)} data-testid={`ben-line-type-${i}`}><option value="primary">Primary</option><option value="contingent">Contingent</option></select>
              <input className="col-span-2" type="number" placeholder="%" value={b.percent} onChange={e=>setBen(i,"percent",e.target.value)} data-testid={`ben-line-pct-${i}`}/>
              <input className="col-span-2" placeholder="Relationship" value={b.relationship} onChange={e=>setBen(i,"relationship",e.target.value)}/>
              <button className="col-span-1 btn-outline text-xs" onClick={()=>delBen(i)}><Trash2 size={12}/></button>
            </div>
          ))}
          <button className="btn-outline text-xs mb-3" onClick={addBen} data-testid="ben-add-line"><Plus size={12} className="inline"/> Add beneficiary</button>
          <textarea placeholder="Notes" value={editing.Notes} onChange={e=>setEditing({...editing,Notes:e.target.value})} rows={2} className="mb-3"/>
          <div className="flex gap-2"><button className="btn-primary" onClick={save} data-testid="ben-save">Save</button><button className="btn-outline" onClick={()=>setEditing(null)}>Cancel</button></div>
        </Card>
      )}

      <div className="space-y-2">
        {(summary?.accounts||[]).map(a=>(
          <Card key={a.id} data-testid={`ben-row-${a.id}`}>
            <div className="flex justify-between items-start gap-3">
              <div>
                <div className="font-medium" style={{color:'var(--primary)'}}>{a.AccountType || "Account"} · {a.Institution} {bChip(a.Status)} {a.complete && <span className="chip chip-green text-xs"><CheckCircle2 size={11} className="inline"/> Confirmed</span>}</div>
                <div className="text-xs mt-1" style={{color:'var(--muted)'}}>Owner: {a.Owner||"—"} · {a.beneficiary_count} beneficiaries · primary {a.primary_total}%{a.contingent_total?` · contingent ${a.contingent_total}%`:""}</div>
                {a.flags.map((f,i)=>(<div key={i} className="text-xs mt-1 flex items-center gap-1" style={{color:'var(--overdue)'}}><AlertTriangle size={11}/>{f}</div>))}
              </div>
              <div className="flex gap-1 shrink-0">
                <button className="btn-outline text-xs" onClick={()=>setEditing(rows.find(r=>r.id===a.id))} data-testid={`ben-edit-${a.id}`}>Edit</button>
                <button className="btn-outline text-xs" style={{color:'var(--overdue)'}} onClick={()=>del(a.id)}><Trash2 size={12}/></button>
              </div>
            </div>
          </Card>
        ))}
        {summary?.accounts.length===0 && <Card><div className="text-sm" style={{color:'var(--muted)'}}>No designations yet.</div></Card>}
      </div>
    </div>
  );
}

// ==================== QUESTION-FIRST POUR-OVER WILL / ESTATE (§8/§9) ====================
const POUR_OVER_QUESTIONS = [
  { k: "testator_name", q: "Full legal name of the testator (person making the will)?", explain: "This is the person whose will this is." },
  { k: "domicile", q: "State/jurisdiction of the testator's legal domicile?", explain: "Will validity and execution formalities depend on domicile." },
  { k: "trust_name", q: "Exact name of the revocable trust that assets pour into?", explain: "The pour-over will directs probate assets into this trust." },
  { k: "trust_date", q: "Date the revocable trust was executed (and any amendment dates)?" },
  { k: "personal_rep", q: "Who is nominated as personal representative / executor (and successor)?" },
  { k: "guardian", q: "If minor children, who is nominated as their guardian?", conditional: "Only if there are minor children." },
  { k: "tangible_memo", q: "Will a separate tangible personal property memorandum be used?" },
  { k: "tax_apportionment", q: "How should death taxes/expenses be apportioned?", explain: "Often 'paid from the residue' — confirm with the attorney." },
  { k: "special", q: "Any special circumstances, disinheritance, or unusual instructions?" },
];

export function EstateDraftingPage() {
  const [rows, setRows] = useState([]);
  const [intake, setIntake] = useState(null); // {answers:{}, id?}
  const load = useCallback(async () => { const r = await A.reg.list("pour_over_wills"); setRows(r.data); }, []);
  useEffect(() => { load(); }, [load]);

  const start = () => setIntake({ answers: {} });
  const setA = (k, v) => setIntake({ ...intake, answers: { ...intake.answers, [k]: v } });
  const answered = POUR_OVER_QUESTIONS.filter(q => (intake?.answers?.[q.k] || "").trim()).length;
  const intakeComplete = answered >= POUR_OVER_QUESTIONS.filter(q => !q.conditional).length;

  const genBody = (a) => `# POUR-OVER WILL — INTERNAL DRAFT
## DRAFT — NOT EXECUTED · ATTORNEY REVIEW REQUIRED · NOT LEGAL ADVICE

Testator: ${a.testator_name || "[Awaiting Trustee Input]"}
Domicile: ${a.domicile || "[Awaiting Trustee Input]"}

I, ${a.testator_name || "[Testator]"}, a resident of ${a.domicile || "[jurisdiction]"}, declare this to be my Last Will and Testament, revoking all prior wills and codicils.

ARTICLE I — POUR-OVER. I give my residuary estate to the trustee of the ${a.trust_name || "[Revocable Trust name]"} dated ${a.trust_date || "[date]"}, to be added to and administered as part of that trust.

ARTICLE II — PERSONAL REPRESENTATIVE. I nominate ${a.personal_rep || "[personal representative]"} as personal representative.
${a.guardian ? `ARTICLE III — GUARDIAN. I nominate ${a.guardian} as guardian of my minor children.\n` : ""}ARTICLE IV — TANGIBLE PROPERTY. ${a.tangible_memo ? "A separate tangible personal property memorandum may be used." : "[Tangible property disposition — Awaiting Trustee Input]"}
ARTICLE V — TAXES. ${a.tax_apportionment || "[Tax apportionment — Awaiting Trustee Input]"}
${a.special ? `ARTICLE VI — SPECIAL PROVISIONS. ${a.special}\n` : ""}
--- This is a NON-EXECUTED internal draft. It is not valid until reviewed by a licensed attorney and executed with the formalities required in the testator's jurisdiction (typically signature + witnesses + notarization). A pour-over will does NOT avoid probate for probate assets and does NOT fund an unfunded trust. ---`;

  const saveDraft = async () => {
    const a = intake.answers;
    const body = genBody(a);
    const payload = {
      Title: `Pour-Over Will — ${a.testator_name || "Draft"}`,
      Answers: a, Body: body,
      Status: intakeComplete ? "Draft Generated" : "Intake Incomplete",
      Notes: "DRAFT — attorney review required; not executed.",
    };
    try {
      if (intake.id) { const id = intake.id; await A.reg.patch("pour_over_wills", id, payload); }
      else await A.reg.create("pour_over_wills", payload);
      toast.success(intakeComplete ? "Draft generated (attorney review required)" : "Intake saved (incomplete)");
      setIntake(null); load();
    } catch (e) { toast.error("Save failed"); }
  };

  return (
    <div data-testid="estate-drafting-page">
      <H1>Pour-Over Will — Question-First Drafting</H1>
      <Sub>Answer plain-language questions first; the Portal then assembles a clearly-labeled internal draft. Drafts can never be marked valid, executed, or attorney-approved by the Portal — those require a licensed professional and proper execution.</Sub>

      <div className="banner-warning mb-4 text-xs">A pour-over will does not avoid probate for probate assets, does not fund an unfunded trust, and is not valid without compliant execution. Attorney review is required.</div>

      {!intake ? (
        <>
          <button className="btn-gold mb-4 flex items-center gap-1" onClick={start} data-testid="estate-new"><ScrollText size={15}/> Start a pour-over will intake</button>
          <H2>Existing drafts</H2>
          <div className="space-y-2">
            {rows.map(r=>(
              <Card key={r.id} data-testid={`estate-row-${r.id}`}>
                <div className="flex justify-between items-center">
                  <div><div className="font-medium" style={{color:'var(--primary)'}}>{r.Title}</div><div className="text-xs" style={{color:'var(--muted)'}}>Status: {r.Status}</div></div>
                  <button className="btn-outline text-xs" onClick={()=>setIntake({answers:r.Answers||{},id:r.id})} data-testid={`estate-open-${r.id}`}>Open intake</button>
                </div>
              </Card>
            ))}
            {rows.length===0 && <Card><div className="text-sm" style={{color:'var(--muted)'}}>No drafts yet.</div></Card>}
          </div>
        </>
      ) : (
        <Card data-testid="estate-intake">
          <div className="flex justify-between items-center mb-3">
            <div className="font-display text-lg">Intake questionnaire</div>
            <div className="text-xs" style={{color:'var(--muted)'}}>{answered}/{POUR_OVER_QUESTIONS.length} answered · {intakeComplete ? <span className="chip chip-green text-xs">Intake complete</span> : <span className="chip chip-amber text-xs">Intake incomplete</span>}</div>
          </div>
          {POUR_OVER_QUESTIONS.map(q=>(
            <div key={q.k} className="mb-3">
              <label className="text-sm font-medium">{q.q}{q.conditional && <span className="text-xs ml-1" style={{color:'var(--muted)'}}>({q.conditional})</span>}</label>
              {q.explain && <div className="text-xs mb-1" style={{color:'var(--muted)'}}>{q.explain}</div>}
              <input value={intake.answers[q.k]||""} onChange={e=>setA(q.k,e.target.value)} data-testid={`estate-q-${q.k}`} placeholder="Answer, or leave blank / write 'Unknown' / 'Professional Review Required'"/>
            </div>
          ))}
          <div className="flex gap-2 mt-4">
            <button className="btn-primary" onClick={saveDraft} data-testid="estate-generate">{intakeComplete ? "Generate draft" : "Save incomplete intake"}</button>
            <button className="btn-outline" onClick={()=>setIntake(null)}>Cancel</button>
          </div>
        </Card>
      )}
    </div>
  );
}
